import { Draft, PayloadAction } from "@reduxjs/toolkit";

import { AutocompleteItem } from "../../../components/Autocomplete";
import { FormId } from "../../../components/enums";
import { defaultCountry } from "../../../shared";
import {
  createGenericUISlice,
  GenericState,
  UIState,
} from "../../../store/HOC/genericUI.slice";

import { ComponentId as AdditionalComponentId } from "./AdditionalData/enums";
import { ComponentId as ApplicantComponentId } from "./ApplicantData/enums";
import { ComponentId as DriverComponentId } from "./DriverData/enums";

export type FormDataInputs = {
  [FormId.APPLICANT]: {
    [ApplicantComponentId.APPLICANT_NAME]: string;
    [ApplicantComponentId.APPLICANT_SURNAME]: string;
    [ApplicantComponentId.APPLICANT_PHONE]: string;
    [ApplicantComponentId.APPLICANT_EMAIL]: string;
    [ApplicantComponentId.APPLICANT_IS_DRIVER]: string;
  };
  [FormId.APPLICANT_DRIVER]: {
    [DriverComponentId.APPLICANT_DRIVER_NAME]: string;
    [DriverComponentId.APPLICANT_DRIVER_SURNAME]: string;
    [DriverComponentId.APPLICANT_DRIVER_PHONE]: string;
    [DriverComponentId.APPLICANT_DRIVER_EMAIL]: string;
    [DriverComponentId.APPLICANT_DRIVER_PESEL]: string;
    [DriverComponentId.APPLICANT_DRIVER_DRIVING_LICENSE]: string;
    [DriverComponentId.APPLICANT_DRIVER_LACK_OF_PESEL]: string | boolean;
    [DriverComponentId.APPLICANT_DRIVER_STREET]: string;
    [DriverComponentId.APPLICANT_DRIVER_STREET_2]: string;
    [DriverComponentId.APPLICANT_DRIVER_ZIP_CODE]: string;
    [DriverComponentId.APPLICANT_DRIVER_CITY]: string;
    [DriverComponentId.APPLICANT_DRIVER_COUNTRY]: AutocompleteItem;
  };
  [FormId.APPLICANT_ADDITIONAL]: {
    [AdditionalComponentId.APPLICANT_ADDITIONAL_ARE_OTHER_PEOPLE_INVOLVED]: string;
    [AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS]: {
      [key: string]: { [key: string]: string };
    };
  };
};

export type ApplicantFormInputs = FormDataInputs[FormId.APPLICANT];
export type ApplicantDriverFormInputs = FormDataInputs[FormId.APPLICANT_DRIVER];
export type ApplicantAdditionalFormInputs =
  FormDataInputs[FormId.APPLICANT_ADDITIONAL];

type ApplicantState = {
  data: FormDataInputs;
  ui: UIState;
};

export const initialState: ApplicantState = {
  data: {
    [FormId.APPLICANT]: {
      [ApplicantComponentId.APPLICANT_NAME]: "",
      [ApplicantComponentId.APPLICANT_SURNAME]: "",
      [ApplicantComponentId.APPLICANT_PHONE]: "",
      [ApplicantComponentId.APPLICANT_EMAIL]: "",
      [ApplicantComponentId.APPLICANT_IS_DRIVER]: "",
    },
    [FormId.APPLICANT_DRIVER]: {
      [DriverComponentId.APPLICANT_DRIVER_NAME]: "",
      [DriverComponentId.APPLICANT_DRIVER_SURNAME]: "",
      [DriverComponentId.APPLICANT_DRIVER_PHONE]: "",
      [DriverComponentId.APPLICANT_DRIVER_EMAIL]: "",
      [DriverComponentId.APPLICANT_DRIVER_PESEL]: "",
      [DriverComponentId.APPLICANT_DRIVER_DRIVING_LICENSE]: "",
      [DriverComponentId.APPLICANT_DRIVER_LACK_OF_PESEL]: false,
      [DriverComponentId.APPLICANT_DRIVER_STREET]: "",
      [DriverComponentId.APPLICANT_DRIVER_STREET_2]: "",
      [DriverComponentId.APPLICANT_DRIVER_ZIP_CODE]: "",
      [DriverComponentId.APPLICANT_DRIVER_CITY]: "",
      [DriverComponentId.APPLICANT_DRIVER_COUNTRY]: {
        ...defaultCountry,
      },
    },
    [FormId.APPLICANT_ADDITIONAL]: {
      [AdditionalComponentId.APPLICANT_ADDITIONAL_ARE_OTHER_PEOPLE_INVOLVED]:
        "false",
      [AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS]: {},
    },
  },
  ui: {
    validated: false,
    [DriverComponentId.APPLICANT_DRIVER_COUNTRY]: defaultCountry.value,
  },
};

const applicantSlice = createGenericUISlice({
  name: "@AC/applicant",
  initialState: initialState as GenericState<FormDataInputs>,
  reducers: {
    setData: (state, action: PayloadAction<FormDataInputs>) => {
      const {
        payload: {
          [FormId.APPLICANT_ADDITIONAL]: { persons, ...restAdditional },
        },
      } = action;

      state.data[FormId.APPLICANT] = action.payload[FormId.APPLICANT];
      state.data[FormId.APPLICANT_DRIVER] = {
        ...initialState.data[FormId.APPLICANT_DRIVER],
        ...action.payload[FormId.APPLICANT_DRIVER],
      };
      state.data[FormId.APPLICANT_ADDITIONAL] = {
        [AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS]: persons
          ? persons
          : initialState.data[FormId.APPLICANT_ADDITIONAL][
              AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS
            ],
        ...restAdditional,
      };
      state.ui.validated = true;
    },
    setAvailableCities: (
      state,
      action: PayloadAction<{ label: string; value: string }[]>
    ) => {
      state.ui["applicantAvailableCities"] = action.payload;
    },
    setLackOfPesel: (state, action: PayloadAction<string>) => {
      state.data[FormId.APPLICANT_DRIVER][
        DriverComponentId.APPLICANT_DRIVER_LACK_OF_PESEL
      ] = action.payload;
    },
    setPersonsAreInvolved: (state, action: PayloadAction<string>) => {
      state.data[FormId.APPLICANT_ADDITIONAL][
        AdditionalComponentId.APPLICANT_ADDITIONAL_ARE_OTHER_PEOPLE_INVOLVED
      ] = action.payload;
      state.data[FormId.APPLICANT_ADDITIONAL][
        AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS
      ] =
        initialState.data[FormId.APPLICANT_ADDITIONAL][
          AdditionalComponentId.APPLICANT_ADDITIONAL_PERSONS
        ];
    },
    copyApplicantToDriver: (
      state,
      action: PayloadAction<
        Partial<FormDataInputs[FormId.APPLICANT_DRIVER]> & {
          countryValidator: string;
          [ApplicantComponentId.APPLICANT_IS_DRIVER]: string;
        }
      >
    ) => {
      const { isDriver, countryValidator, ...restPayload } = action.payload;

      state.data[FormId.APPLICANT][ApplicantComponentId.APPLICANT_IS_DRIVER] =
        isDriver;
      state.data[FormId.APPLICANT_DRIVER] = {
        ...state.data[FormId.APPLICANT_DRIVER],
        ...restPayload,
      };

      if (!("driverphone" in state.ui)) {
        state.ui.driverphone = {
          isActive: true,
        };
      }

      state.ui.driverphone = {
        ...(state.ui.driverphone as Draft<Record<string, unknown>>),
        country: countryValidator,
      };
    },
  },
});

export const {
  copyApplicantToDriver,
  setData: setApplicantFormData,
  setLackOfPesel,
  setPersonsAreInvolved,
  setUIField,
  setUIObjectField,
  setAvailableCities,
} = applicantSlice.actions;

export const { reducer } = applicantSlice;
